import { CaretRightOutlined, CheckCircleOutlined, ClearOutlined, CloseCircleOutlined, PauseOutlined, ReloadOutlined, SettingOutlined, WarningOutlined } from '@ant-design/icons';
import { Button, Tooltip, Typography } from 'antd';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { wsService } from '../../../services/websocket';
import { WSConnectionStatus } from '../../../types/websocket';

const { Title } = Typography;

const AnimatedPlayIcons: React.FC = () => {
  return (
    <span className="animated-play-icons">
      <CaretRightOutlined className="play-icon play-icon-1" />
      <CaretRightOutlined className="play-icon play-icon-2" />
      <CaretRightOutlined className="play-icon play-icon-3" />
    </span>
  );
};

type HeaderBarProps = {
  loading: boolean;
  onRefresh: () => void | Promise<void>;
  isPaused: boolean;
  onPauseToggle: () => void;
  onClear: () => void;
  autoTabOpeningEnabled: boolean;
  onAutoTabToggle: () => void;
  autoOpeningWhitelistOnly: boolean;
  onAutoOpeningWhitelistToggle: () => void;
  tabCount: number;
  isHoveringPair: boolean;
};

const HeaderBar: React.FC<HeaderBarProps> = ({
  loading,
  onRefresh,
  isPaused,
  onPauseToggle,
  onClear,
  autoTabOpeningEnabled,
  onAutoTabToggle,
  autoOpeningWhitelistOnly,
  onAutoOpeningWhitelistToggle,
  tabCount,
  isHoveringPair,
}) => {
  const navigate = useNavigate();
  const [connectionStatus, setConnectionStatus] = useState<WSConnectionStatus>(wsService.getConnectionStatus());

  useEffect(() => {
    const unsubscribe = wsService.onStatusChange((status) => {
      setConnectionStatus(status);
    });
    return unsubscribe;
  }, []);

  const getStatusIcon = () => {
    if (connectionStatus === 'error' || connectionStatus === 'disconnected') {
      return <WarningOutlined style={{ color: '#ff4d4f' }} />;
    }
    if (isPaused || isHoveringPair) {
      return <PauseOutlined style={{ color: '#8c8c8c' }} />;
    }
    if (connectionStatus === 'connected') {
      return <AnimatedPlayIcons />;
    }
    // connecting or other states
    return <PauseOutlined style={{ color: '#8c8c8c' }} />;
  };

  const getStatusTooltip = () => {
    if (connectionStatus === 'error' || connectionStatus === 'disconnected') {
      return 'Connection issue: WebSocket is disconnected or has an error. New pairs may not be received.';
    }
    if (isPaused) {
      return 'Paused: Listening is paused. New pairs will not be processed until resumed.';
    }
    if (isHoveringPair) {
      return 'Paused: Hovering over a pair. New pairs will not be processed until you move the mouse away.';
    }
    if (connectionStatus === 'connected') {
      return 'Active: Connection is active and listening for new pairs.';
    }
    if (connectionStatus === 'connecting') {
      return 'Connecting: Establishing WebSocket connection...';
    }
    return 'Status unknown';
  };

  return (
    <div className="axiom-pair-header">
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Title level={4} style={{ margin: 0 }}>
          Latest Pair Records
        </Title>
        <Tooltip
          title={"Paused as you are hovering pair!"}
          placement="top"
          open={!!isHoveringPair}
        >
          <span style={{ fontSize: '18px', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
            {getStatusIcon()}
          </span>
        </Tooltip>
      </div>
      <div className="axiom-pair-header__controls">
        <Button.Group>
          <Tooltip title="Auto open tabs for pairs that match filters or are whitelisted (independently of whether the whitelist button is checked or not)." placement="top">
            <Button
              icon={autoTabOpeningEnabled ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
              onClick={onAutoTabToggle}
              type={autoTabOpeningEnabled ? 'primary' : 'default'}
            >
              Auto Open Filtered
            </Button>
          </Tooltip>
          <Tooltip title="Auto open tabs only for whitelisted pair addresses." placement="top">
            <Button
              icon={autoOpeningWhitelistOnly ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
              onClick={onAutoOpeningWhitelistToggle}
              type={autoOpeningWhitelistOnly ? 'primary' : 'default'}
            >
              Auto Open Whitelisted
            </Button>
          </Tooltip>
          <Button
            icon={<SettingOutlined />}
            onClick={() => navigate('/settings')}
          >
            Settings
          </Button>
        </Button.Group>
        <Button.Group>
          <Tooltip title="Clear" placement="top">
            <Button
              icon={<ClearOutlined />}
              onClick={onClear}
            />
          </Tooltip>
          <Tooltip title="Pause" placement="top">
            <Button
              icon={<PauseOutlined />}
              onClick={onPauseToggle}
              type={isPaused ? 'primary' : 'default'}
            />
          </Tooltip>
          <Tooltip title="Refresh" placement="top">
            <Button
              icon={<ReloadOutlined />}
              onClick={onRefresh}
              loading={loading}
            />
          </Tooltip>
        </Button.Group>
      </div>
    </div>
  );
};

export default HeaderBar;
